import { useAuth0 } from '@auth0/auth0-react';
import { Center, Loader } from '@qred/components-library';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { history } from '~/constants';
import { AuthProvider } from '~/enums';
import { accessToken } from '~/store/apis/utils/accessToken';
import { AuthContext, IAuthContext } from './AuthContext';
import constVars from '~/constants/constVars';

export const Auth0AuthProvider = ({ children }: { children: ReactNode }) => {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: auth0IsLoading,
    logout: auth0Logout,
    loginWithRedirect,
  } = useAuth0();

  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    isLoading: true,
  });

  const signInWithRedirect = useCallback(
    (options: Parameters<NonNullable<IAuthContext['signInWithRedirect']>>[0]) =>
      loginWithRedirect({
        authorizationParams: {
          connection: options.connection,
          language: options.language,
          market: options.market,
          ...(options.redirectUri && {
            redirect_uri: options.redirectUri,
          }),
        },
        appState: { returnTo: options.returnTo },
      }),
    [loginWithRedirect]
  );

  const logout = useCallback(() => {
    return auth0Logout({
      logoutParams: {
        returnTo: `${globalThis.location.origin}/${constVars.ROUTE_LOGIN}`,
      },
    });
  }, []);

  const auth = useMemo<IAuthContext>(
    () => ({
      provider: AuthProvider.AUTH0,
      ...authState,
      logout,
      signInWithRedirect,
    }),
    [authState, logout, signInWithRedirect]
  );

  const fetchAuth0AccessToken = async (): Promise<string> => {
    return getAccessTokenSilently();
  };

  useEffect(() => {
    const handleAuthenticationStatus = async () => {
      const shouldBeAuthenticated =
        localStorage.getItem('isAuthenticated') === '1';

      if (!isAuthenticated && shouldBeAuthenticated) {
        const url = new URL(globalThis.location.href);
        const errorParam = url.searchParams.get('error');
        if (errorParam === 'login_required') {
          localStorage.removeItem('isAuthenticated');
          url.searchParams.delete('error');
          url.searchParams.delete('error_description');
          url.searchParams.delete('state');
          history.replace({
            search: url.search,
          });
        } else {
          sessionStorage.setItem(
            'returnTo',
            `${globalThis.location.pathname}${globalThis.location.search}`
          );

          return await loginWithRedirect({
            authorizationParams: {
              prompt: 'none',
            },
            appState: {
              returnTo: `${globalThis.location.pathname}${globalThis.location.search}`,
            },
          });
        }
      } else if (isAuthenticated) {
        accessToken.set(fetchAuth0AccessToken);
        // Set local storage to indicate that the user is authenticated
        // This is used to attempt silent authentication on page load in browsers
        // that blocks third-party cookies (like Safari and Firefox by default)
        localStorage.setItem('isAuthenticated', '1');
      }

      setAuthState({ isAuthenticated, isLoading: auth0IsLoading });
    };

    if (!auth0IsLoading) {
      handleAuthenticationStatus();
    }
  }, [isAuthenticated, auth0IsLoading]);

  if (authState.isLoading) {
    return (
      <Center>
        <Loader size={'xxl'} />
      </Center>
    );
  }

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
